/**
 * A skill's evidence, duplicates, origin and use in words (docs/plans/2026-10-odysseus-adoption.md,
 * items 2 and 3). The shapes are `@aioe/shared` catalogue.ts; nothing here shows an id or a code.
 */

import { unusedSkill, type CatalogueEntry, type CatalogueEvaluation, type CatalogueSubmission } from '@aioe/shared';
import { dateLong } from '@aioe/shared/wording/dateTime';

/** "just now", "3 hours ago", "yesterday", "2 days ago". */
export function whenWords(iso: string, now = Date.now()): string {
  const s = Math.max(0, (now - Date.parse(iso)) / 1000);
  if (s < 90) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)} minutes ago`;
  if (s < 86400) {
    const h = Math.round(s / 3600);
    return h === 1 ? 'an hour ago' : `${h} hours ago`;
  }
  const d = Math.round(s / 86400);
  return d === 1 ? 'yesterday' : `${d} days ago`;
}

const sentence = (s: string) => (s.trim() ? `${s.trim().replace(/[.;,\s]+$/, '')}.` : '');

/** The judge's line: "Helped in 4 of 5 tasks; the model managed 3 of 5 without it; 20 per cent fewer tokens." */
export const evidenceSentence = (e: CatalogueEvaluation): string => sentence(e.result.sentence);

/** "Evaluated 2 days ago on mju-gpu-1 with qwen3-14b, judged by claude-sonnet-4-5." */
export const evidenceMeta = (e: CatalogueEvaluation, now?: number): string => `Evaluated ${whenWords(e.at, now)} on ${e.deviceName} with ${e.worker}, judged by ${e.judge}.`;

/** "Loaded 14 times in 90 days" / "Loaded once in 90 days" / "Not loaded in 90 days"; undefined when not counted. */
/** "Not used since 3 September" or "Not used in 90 days" for a skill offered for archiving (Hermes item 6); undefined otherwise. */
export function unusedWords(e: Pick<CatalogueEntry, 'kind' | 'status' | 'pinned' | 'usedByTeams' | 'loads90d' | 'lastLoadedAt' | 'changedAt'>, nowIso: string): string | undefined {
  if (!unusedSkill(e, nowIso)) return undefined;
  return e.lastLoadedAt ? `Not used since ${dateLong(e.lastLoadedAt, { year: false })}` : 'Not used in 90 days';
}

export function loadsWords(loads90d: number | undefined): string | undefined {
  if (loads90d === undefined) return undefined;
  if (loads90d === 0) return 'Not loaded in 90 days';
  return loads90d === 1 ? 'Loaded once in 90 days' : `Loaded ${loads90d.toLocaleString()} times in 90 days`;
}

/** "Looks like 'Release notes' (78 per cent similar)", and how many more. */
export function similarWords(similar: CatalogueSubmission['similar']): string | undefined {
  const first = similar?.[0];
  if (!first) return undefined;
  const more = similar!.length - 1;
  return `Looks like '${first.name}' (${Math.round(first.score * 100)} per cent similar)${more ? ` and ${more} more published ${more === 1 ? 'skill' : 'skills'}` : ''}`;
}

/** "Drafted from T-14 on Dana's workbench"; undefined when the submission says nothing of where it came from. */
export function originWords(s: Pick<CatalogueSubmission, 'origin' | 'submittedByName' | 'submittedBy'>): string | undefined {
  const o = s.origin;
  if (!o) return undefined;
  if (o.designed) return `Designed in a conversation on ${s.submittedByName ? `${s.submittedByName.split(/[\s@]/)[0]}'s workbench` : 'a workbench'}`;
  if (!o.drafted && !o.taskId && !o.runId) return undefined;
  const from = o.taskId ?? (o.runId ? 'a run' : 'their work');
  const who = s.submittedByName ? `${s.submittedByName.split(/[\s@]/)[0]}'s workbench` : 'a workbench';
  return `${o.drafted ? 'Drafted from' : 'From'} ${from} on ${who}`;
}
